import React from 'react';
import Button from './Button';

/**
 * Empty state component for "No events", "No applications" etc.
 * Design: High-contrast, minimal, centered
 */
export default function EmptyState({
  icon,
  title,
  description,
  actionLabel,
  onAction,
  className = '',
}) {
  return (
    <div className={`flex flex-col items-center justify-center py-16 px-8 ${className}`}>
      {/* Icon */}
      {icon && (
        <div className="w-16 h-16 mb-6 border-2 border-black flex items-center justify-center">
          {icon}
        </div>
      )}

      {/* Title */}
      <h3 className="font-mono text-lg font-bold uppercase tracking-wider text-black mb-2 text-center">
        {title}
      </h3>

      {/* Description */}
      {description && (
        <p className="font-mono text-sm text-gray-600 text-center max-w-md mb-6">
          {description}
        </p>
      )}

      {/* Action button */}
      {actionLabel && onAction && (
        <Button variant="primary" onClick={onAction}>
          {actionLabel}
        </Button>
      )}
    </div>
  );
}

/**
 * Pre-configured empty states for common scenarios
 */
export function NoEventsState({ onCreateEvent }) {
  return (
    <EmptyState
      icon={
        <svg className="w-8 h-8 text-black" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="square" strokeLinejoin="miter" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
      }
      title="NO EVENTS YET"
      description="You haven't created any events. Start your first event to begin accepting applications."
      actionLabel="CREATE EVENT"
      onAction={onCreateEvent}
    />
  );
}

export function NoApplicationsState({ type = 'received' }) {
  return (
    <EmptyState
      icon={
        <svg className="w-8 h-8 text-black" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="square" strokeLinejoin="miter" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
      }
      title={type === 'received' ? 'NO APPLICATIONS YET' : 'NO INVITATIONS YET'}
      description={
        type === 'received'
          ? 'No one has applied to your events yet. Share your event to attract applicants.'
          : 'No invitations yet. Apply to events you like — hosts see your profile when you do.'
      }
    />
  );
}

export function NoCredentialsState({ onBrowseEvents }) {
  return (
    <EmptyState
      icon={
        <svg className="w-8 h-8 text-black" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="square" strokeLinejoin="miter" strokeWidth={2} d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z" />
        </svg>
      }
      title="NO CREDENTIALS YET"
      description="Your Portal Page is empty. Attend events to earn credentials and build your reputation."
      actionLabel="BROWSE EVENTS"
      onAction={onBrowseEvents}
    />
  );
}

export function NoResultsState({ searchQuery, onClearSearch }) {
  return (
    <EmptyState
      icon={
        <svg className="w-8 h-8 text-black" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="square" strokeLinejoin="miter" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
        </svg>
      }
      title="NO RESULTS FOUND"
      description={searchQuery ? `No results for "${searchQuery}". Try a different search term.` : 'No results match your filters.'}
      actionLabel="CLEAR SEARCH"
      onAction={onClearSearch}
    />
  );
}

export function ErrorState({ message, onRetry }) {
  return (
    <EmptyState
      icon={
        <svg className="w-8 h-8 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="square" strokeLinejoin="miter" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
        </svg>
      }
      title="SOMETHING WENT WRONG"
      description={message || 'An error occurred. Please try again.'}
      actionLabel="TRY AGAIN"
      onAction={onRetry}
    />
  );
}
